// Public Support page — modernized for Roava 2026.
// PUBLIC: no auth. Used as App Store Connect Support URL.
// URL: https://roava.wgpapp.com/support

import { createFileRoute, Link } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { MarketingShell } from "@/components/marketing/MarketingShell";

const SUPPORT_EMAIL = "support@watsongrowthpartners.com";

export const Route = createFileRoute("/support")({
  head: () =>
    pageMeta({
      title: "Support — Roava",
      description:
        "Get help with Roava: trip access, offline maps, GPS tracking, photo upload, Family Follow, widgets, notifications, HealthKit, and account deletion.",
      path: "/support",
    }),
  component: SupportPage,
});

function SupportPage() {
  return (
    <MarketingShell>
      <article className="mx-auto max-w-2xl pt-2">
        <header className="mb-8">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Help</p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">Support</h1>
          <p className="mt-3 text-base text-muted-foreground">
            We're a small team and we read every message.
          </p>
        </header>

        <section className="rounded-2xl border border-border bg-card/60 p-5 sm:p-6 shadow-sm">
          <h2 className="text-lg font-semibold">Email us</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Questions, bug reports, feature feedback, account or data requests:
          </p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="mt-3 inline-flex items-center rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold shadow-sm hover:opacity-90 transition"
          >
            {SUPPORT_EMAIL}
          </a>
          <p className="mt-3 text-xs text-muted-foreground">
            We will get back to you as soon as we can — typically within <strong>7–10 business days</strong>, though it can take longer depending on the number of requests. Please include your
            device model, iOS version, and a short description of what you were doing.
          </p>
        </section>

        <section className="mt-10">
          <h2 className="text-xl font-semibold tracking-tight">Common topics</h2>

          <FaqItem question="Invite code or account access">
            Roava is invite-only. Get your invite code from the trip organizer and enter it
            on the sign-in screen. If your code is rejected, double-check capitalization
            and trailing spaces, or ask your organizer to re-issue it.
          </FaqItem>

          <FaqItem question="Offline map download issues">
            Offline maps download in the background and resume after interruptions. If a
            download is stuck, make sure the app has connectivity, then re-open the trip's
            map screen — Roava will retry automatically. Free up device storage if the
            download keeps failing.
          </FaqItem>

          <FaqItem question="GPS route tracking gaps">
            Tracking gaps usually mean iOS paused background location while the app was
            inactive in low-signal areas. Re-opening Roava resumes recording and syncs any
            points captured offline. Check that "Always" location permission is granted.
          </FaqItem>

          <FaqItem question="Background location permissions">
            For continuous trip recording, Roava needs the "Always" location permission.
            Open <em>Settings → Roava → Location</em> and choose <strong>Always</strong>.
            Precise Location should also be on for accurate routes.
          </FaqItem>

          <FaqItem question="Queued or offline sync">
            Routes, photos, and updates captured offline are queued on your device and sync
            automatically when connectivity returns. You can also tap <strong>Sync now</strong>{" "}
            from the home or profile screen to drain the queue manually.
          </FaqItem>

          <FaqItem question="Photo or video upload retry">
            Uploads retry automatically on connectivity changes. If an upload keeps failing,
            check available storage and that Roava has Photos access in iOS Settings, then
            tap "Sync now". Large videos may take several minutes on a weak connection.
          </FaqItem>

          <FaqItem question="Family Follow links">
            From an active trip, generate a Family Follow share link. The recipient opens
            the link in a browser to see the trip view you've chosen to share. You can
            revoke the link at any time from inside the app.
          </FaqItem>

          <FaqItem question="Live Activity, widgets, and Control Center">
            Roava shows trip status on the Lock Screen, in the Dynamic Island, in Home
            Screen widgets, and in Control Center. If the widget or Live Activity isn't
            updating, open the app once to refresh, and confirm Live Activities are enabled
            in <em>Settings → Roava</em>.
          </FaqItem>

          <FaqItem question="Push and local notifications">
            Notifications are optional. Enable or disable them in <em>Settings → Roava → Notifications</em>.
            Local trip reminders work offline; push notifications require connectivity.
          </FaqItem>

          <FaqItem question="HealthKit export">
            If enabled, Roava can attach activity context to a trip via Apple Health. Grant
            permission in <em>Settings → Privacy & Security → Health → Roava</em>. Roava
            does not delete data from Apple Health — manage that in the Health app.
          </FaqItem>

          <FaqItem question="Account and data deletion">
            See our{" "}
            <Link to="/account-deletion" className="text-primary hover:underline">
              Account & Data Deletion
            </Link>{" "}
            page for what gets removed and how to request deletion.
          </FaqItem>
        </section>

        <section className="mt-12 text-sm text-muted-foreground">
          <p>
            See also:{" "}
            <Link to="/privacy" className="text-primary hover:underline">
              Privacy Policy
            </Link>
            {" · "}
            <Link to="/account-deletion" className="text-primary hover:underline">
              Account & Data Deletion
            </Link>
            {" · "}
            <Link to="/terms" className="text-primary hover:underline">
              Terms of Use
            </Link>
          </p>
        </section>
      </article>
    </MarketingShell>
  );
}

function FaqItem({ question, children }: { question: string; children: React.ReactNode }) {
  return (
    <details className="group mt-4 rounded-xl border border-border bg-card/40 p-4 open:bg-card/70 transition-colors">
      <summary className="cursor-pointer list-none flex items-center justify-between gap-3 text-sm sm:text-[15px] font-medium">
        <span>{question}</span>
        <span className="text-muted-foreground transition-transform group-open:rotate-45" aria-hidden>+</span>
      </summary>
      <div className="mt-3 text-sm text-foreground/85 leading-relaxed">{children}</div>
    </details>
  );
}
